import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  stateHasFetchedAllObjectsSuccess,
} from 'utils/sliceHelpers';
import {
  EPermissionTypes,
  type IPermission,
  createPermission,
} from '@timeedit/types';
import api from '../services/api.service';

export type TPermissionState = IDefaultAPIState<IPermission>;

export const initialState: TPermissionState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

// Slice
const slice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    fetchPermissionsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchPermissionsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<IPermission>>
    ) => {
      stateHasFetchedAllObjectsSuccess(payload, state, createPermission);
      finishedLoadingSuccess(state);
    },
    fetchPermissionsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
const permissionsSelector = (state: TRootState) => state.permissions.results;

export const scopesSelector = createSelector([permissionsSelector], (results) =>
  results.filter((permission) => permission.type === EPermissionTypes.SCOPE)
);

export const appPermissionsSelector = createSelector(
  [permissionsSelector],
  (results) =>
    results.filter(
      (permission) => permission.type === EPermissionTypes.APP_PERMISSION
    )
);

// Actions
export const {
  fetchPermissionsRequest,
  fetchPermissionsSuccess,
  fetchPermissionsFailure,
} = slice.actions;

export const fetchPermissions = createAppAsyncThunk(
  'permissions/fetchPermissions',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchPermissionsRequest());
      const result = await api.get({
        endpoint: `/permissions?sortBy=name`,
      });
      dispatch(fetchPermissionsSuccess(result));
    } catch (e) {
      dispatch(fetchPermissionsFailure());
      console.error(e);
    }
  }
);
